// Неликвиды склада — что лежит мёртвым грузом. ТОЛЬКО ЧТЕНИЕ.
//
// ЦИФРЫ ЗДЕСЬ ОБЯЗАНЫ СОВПАДАТЬ С КАБИНЕТОМ РУКОВОДИТЕЛЯ. У exec.cirspb.com есть свой
// экран «Неликвиды», им пользуются те же люди, и определение неликвида взято оттуда
// дословно — считает его ядро (nl_api.py), а не этот файл. Две разные цифры про один
// склад — это не «две точки зрения», это конец доверия к обеим.
//
// ГЛАВНОЕ, ЧТО ЭКРАН ОБЯЗАН СКАЗАТЬ ВСЛУХ (замеры 28.07.2026, пересчитывает ядро):
//   • 1С УТ заведена 29.04.2026. Движений за прошлые годы в ней нет ВОВСЕ. Поэтому
//     «сколько лет лежит» этот раздел не отвечает и отвечать не может — предельный
//     возраст здесь равен возрасту самой базы;
//   • у 14 093 позиций из 14 630 расхода не видели ни разу: их возраст показан с «≥»
//     и упирается в тот же день начала учёта;
//   • из-за этого порог «90+ дней» до 28.07.2026 отвечал «неликвида нет», а 28-го
//     срабатывает разом на 11 832 позициях и 119,9 млн ₽. Умолчание порога здесь
//     60 дней именно поэтому — 90 давало единственный заведомо неверный ответ;
//   • у 6 982 позиций из 14 630 (47,7 %) себестоимости в 1С нет вовсе. Любая сумма
//     занижена, и число таких позиций стоит рядом с каждой суммой.
//
// ВКЛАДКА «РЕЗЕРВ» — ДРУГОЙ ИСТОЧНИК. Резервов в зеркале нет: панель выгружает только
// остаток «в наличии». Регистр резервов читается живьём из 1С, поэтому у вкладки своя
// свежесть и свой отказ (OdataState), как на экране «Склад». Смешивать их свежесть
// с зеркальной нельзя — получилось бы обещание, которого раздел не держит.
import { useMemo, useState } from 'react'

import { useNLПозиции, useNLRefs, useNLСвод, useЗаказыПодРезерв, useРезерв } from '../api/nl'
import { FilterBar } from '../components/FilterBar'
import { OdataFreshness, OdataState } from '../components/OdataState'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtDays, fmtMoney, plural, pluralN } from '../lib/format'
import {
  ВАЖНЫЕ_ОГОВОРКИ, ПОДПИСЬ_ВОЗРАСТА, вНорме, возрастом, недосчёт, подписьПорога, свежесть,
} from '../lib/nl'
import type { Позиция, СправочникиNL } from '../lib/nl'

type Вкладка = 'obzor' | 'items' | 'reserve'

const РАЗМЕР_СТРАНИЦЫ = 100

export default function Nelikvid() {
  const [вкладка, setВкладка] = useState<Вкладка>('obzor')
  const refs = useNLRefs()

  const [дней, setДней] = useState<number | null>(null)
  const [регион, setРегион] = useState('')
  const [склад, setСклад] = useState('')
  const [поиск, setПоиск] = useState('')

  // Порог берём у ядра, а не пишем числом: умолчание — часть определения, и жить оно
  // должно там же, где формула.
  const порог = дней ?? refs.data?.default_days ?? 60
  const фильтры = useMemo(
    () => ({ days: порог, region: регион || undefined, wh: склад || undefined,
             q: поиск.trim().length >= 2 ? поиск.trim() : undefined }),
    [порог, регион, склад, поиск],
  )

  const св = свежесть(refs.data?.oldest)

  return (
    <>
      <div className="page-head">
        <h1>Неликвиды</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${св.minutes < 1 ? 'только что' : `${св.minutes} мин назад`}`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Те же цифры, что в кабинете руководителя.</b> Возраст позиции считается от
        последнего расхода; если расхода не было — от последнего прихода; если не было
        и его — от дня, когда завели учёт в 1С
        {refs.data ? ` (${fmtDate(refs.data.history_from)})` : ''}. Позиции, по которым
        расхода не видели ни разу, помечены знаком «≥»: они лежат <i>не меньше</i>
        показанного, а сколько именно — по этим данным не узнать.
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([
          ['obzor', 'Обзор'],
          ['items', `Позиции${refs.data ? ` · ${refs.data.counts.positions}` : ''}`],
          ['reserve', 'Резерв под заказ'],
        ] as [Вкладка, string][]).map(([код, подпись]) => (
          <button key={код} onClick={() => setВкладка(код)}
                  aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      {/* Поля идут блоками `.f` с отдельным <label htmlFor>, а не полем внутри подписи:
          FilterBar берёт подпись из textContent метки, и у метки, внутри которой лежит
          список, в textContent попадают ВСЕ его варианты — в заголовке свёрнутого блока
          оказался бы весь справочник складов. */}
      {вкладка !== 'reserve' && (
        <FilterBar id="nl" style={{ marginBottom: 12 }}>
          <div className="f">
            <label htmlFor="nl-days">Порог</label>
            <select id="nl-days" value={порог} onChange={(e) => setДней(Number(e.target.value))}>
              {(refs.data?.thresholds ?? [30, 60, 90, 180, 365]).map((d) => (
                <option key={d} value={d}>{подписьПорога(d)}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="nl-region">Регион</label>
            <select id="nl-region" value={регион}
                    onChange={(e) => { setРегион(e.target.value); setСклад('') }}>
              <option value="">Все регионы</option>
              {(refs.data?.regions ?? []).map((r) => (
                <option key={r.region} value={r.region}>{r.region} · {r.positions}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="nl-wh">Склад</label>
            <select id="nl-wh" value={склад} onChange={(e) => setСклад(e.target.value)}>
              <option value="">Все склады</option>
              {(refs.data?.warehouses ?? [])
                .filter((w) => !регион || w.region === регион)
                .map((w) => (
                  <option key={w.wh} value={w.wh}>{w.wh_name} · {w.positions}</option>
                ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="nl-q">Поиск</label>
            <input id="nl-q" value={поиск} onChange={(e) => setПоиск(e.target.value)}
                   placeholder="наименование, артикул или склад" />
          </div>
        </FilterBar>
      )}

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {вкладка === 'obzor' && <Обзор ф={фильтры} refs={refs.data} />}
        {вкладка === 'items' && <Позиции ф={фильтры} />}
        {вкладка === 'reserve' && <Резерв />}
        <Оговорки заметки={refs.data?.data_notes} />
      </QueryState>
    </>
  )
}

/** Оговорки из ядра. Показываются на всех вкладках: без них числа читаются неверно. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  const важные = ВАЖНЫЕ_ОГОВОРКИ.filter((к) => заметки[к])
  if (!важные.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {важные.map((к) => <li key={к}>{заметки[к]}</li>)}
      </ul>
    </div>
  )
}

// ───────────────────────────── обзор ─────────────────────────────

function Обзор({ ф, refs }: { ф: Record<string, unknown>; refs?: СправочникиNL }) {
  const свод = useNLСвод(ф)
  const д = свод.data
  return (
    <QueryState isLoading={свод.isLoading} error={свод.error}>
      {д && (
        <>
          {/* Плитки-итоги общие для CRM (styles.css §7). Раньше здесь стояли четыре
              полноразмерные карточки в контейнере `div.cards` — класса `cards` без
              `table-wrap` в CSS не существует вовсе, поэтому сетки не было и карточки
              выстраивались в столбец на всю ширину, каждая со своим 22px числом. */}
          <div className="tiles" style={{ marginBottom: 12 }}>
            <div className="tile">
              <div className="t-label">Остаток на складах</div>
              <div className="t-sum">{fmtMoney(д.totals.cost)}</div>
              <div className="hint">
                {pluralN(д.totals.positions, 'позиция', 'позиции', 'позиций')}
              </div>
            </div>
            <div className="tile">
              <div className="t-label">
                Не двигалось {д.days}+ {plural(д.days, 'дня', 'дней', 'дней')}
              </div>
              <div className="t-sum">{fmtMoney(д.totals.dead_cost)}</div>
              <div className="hint">
                {pluralN(д.totals.dead_positions, 'позиция', 'позиции', 'позиций')}
                {/* Оговорка стоит вплотную к числу, а не в общем списке внизу: без неё
                    сумма читается как полная, а она полной не бывает. */}
                {д.totals.dead_no_cost > 0 && (
                  <div>{недосчёт(д.totals.dead_positions, д.totals.dead_no_cost)}</div>
                )}
              </div>
            </div>
            <div className="tile">
              <div className="t-label">Доля неликвида</div>
              <div className="t-sum">
                {д.dead_share_pct === null ? '—' : `${д.dead_share_pct} %`}
              </div>
              <div className="hint">
                норма ≤ {д.norm_share_pct} %
                {вНорме(д.dead_share_pct, д.norm_share_pct) ? ' — в норме' : ' — превышение'}
              </div>
            </div>
            <div className="tile">
              <div className="t-label">Без себестоимости</div>
              <div className="t-sum">{д.totals.no_cost}</div>
              <div className="hint">
                позиции заведены в 1С без цены — суммы выше занижены
              </div>
            </div>
          </div>

          {/* Предельный возраст — не украшение, а ответ на вопрос «а за сколько лет?».
              Пока он равен возрасту базы, «лет» в этом разделе не бывает вовсе. */}
          <div className="card hint" style={{ marginBottom: 12 }}>
            Самая давняя позиция не двигалась{' '}
            {д.totals.max_idle === null || д.totals.max_idle === undefined
              ? '— дней'
              : fmtDays(д.totals.max_idle)} — это и есть
            предел, доступный по этим данным: учёт в 1С начат {fmtDate(д.history_from)}.
            У {д.totals.approx_positions}{' '}
            {plural(д.totals.approx_positions, 'позиции', 'позиций', 'позиций')} из{' '}
            {д.totals.positions} расхода не видели ни разу.
            {refs?.moves ? ` История движений в источнике — с ${fmtDate(refs.moves.first_day)}`
              + ` по ${fmtDate(refs.moves.last_day)},`
              + ` ${pluralN(refs.moves.move_rows, 'пара', 'пары', 'пар')}`
              + ` «склад + позиция».` : ''}
          </div>

          <ПоВозрасту свод={д} />
          <ПоРегионам свод={д} />
          <ПоСкладам свод={д} />
        </>
      )}
    </QueryState>
  )
}

function ПоВозрасту({ свод }: { свод: NonNullable<ReturnType<typeof useNLСвод>['data']> }) {
  const макс = Math.max(1, ...свод.by_age.map((b) => b.n))
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>Сколько лежит</b>
      <div className="hint" style={{ marginTop: 4 }}>
        Позиции без единого движения стоят одной группой на дне начала учёта — это видно
        как ступенька, и это свойство данных, а не склада.
      </div>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Возраст</th>
              <th className="num">Позиций</th>
              <th className="num">Из них «≥»</th>
              <th className="num">Себестоимость</th>
            </tr>
          </thead>
          <tbody>
            {свод.by_age.map((b) => (
              <tr key={b.bucket}>
                <td data-label="Возраст" className="nowrap">
                  {ПОДПИСЬ_ВОЗРАСТА[b.bucket] ?? b.bucket}
                  <div className="hint">{b.bucket} дн.</div>
                </td>
                <td className="num" data-label="Позиций">
                  {b.n}
                  {/* Полоска, а не график: на 375px график нечитаем, а полоска остаётся
                      полоской и внутри карточки. */}
                  <div aria-hidden="true" style={{
                    height: 4, marginTop: 3, borderRadius: 2,
                    width: `${Math.round((b.n / макс) * 100)}%`,
                    background: 'currentColor', opacity: 0.35, marginLeft: 'auto',
                  }} />
                </td>
                <td className="num muted" data-label="Из них «≥»">{b.approx_positions}</td>
                <td className="num" data-label="Себестоимость">{fmtMoney(b.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ПоРегионам({ свод }: { свод: NonNullable<ReturnType<typeof useNLСвод>['data']> }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>По регионам</b>
      <div className="hint" style={{ marginTop: 4 }}>
        Регион — папка верхнего уровня в справочнике складов 1С. Своего поля «регион»
        у склада там нет, поэтому склад, лежащий в корне, попадает в «(без региона)».
      </div>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Регион</th>
              <th className="num">Позиций</th>
              <th className="num">Остаток</th>
              <th className="num">Не двигалось</th>
              <th className="num">Заморожено</th>
              <th className="num">Доля</th>
            </tr>
          </thead>
          <tbody>
            {свод.by_region.map((r) => {
              const доля = r.cost ? Math.round((1000 * r.dead_cost) / r.cost) / 10 : null
              return (
                <tr key={r.region || '(пусто)'}>
                  <td data-label="Регион">{r.region || '(без региона)'}</td>
                  <td className="num muted" data-label="Позиций">{r.positions}</td>
                  <td className="num" data-label="Остаток">{fmtMoney(r.cost)}</td>
                  <td className="num" data-label="Не двигалось">{r.dead_positions}</td>
                  <td className="num" data-label="Заморожено">{fmtMoney(r.dead_cost)}</td>
                  <td className="num" data-label="Доля">{доля === null ? '—' : `${доля} %`}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ПоСкладам({ свод }: { свод: NonNullable<ReturnType<typeof useNLСвод>['data']> }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>Где лежит: двадцать складов с наибольшей заморозкой</b>
      <div className="table-wrap cards" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th>Склад</th>
              <th>Регион</th>
              <th className="num">Не двигалось</th>
              <th className="num">Заморожено</th>
            </tr>
          </thead>
          <tbody>
            {свод.by_warehouse.map((w) => (
              <tr key={w.wh}>
                <td data-label="Склад">{w.wh_name || w.wh.slice(0, 8)}</td>
                <td className="muted" data-label="Регион">{w.region || '(без региона)'}</td>
                <td className="num" data-label="Не двигалось">
                  {w.dead_positions} <span className="muted">из {w.positions}</span>
                </td>
                <td className="num" data-label="Заморожено">{fmtMoney(w.dead_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ───────────────────────────── позиции ─────────────────────────────

const СОРТИРОВКИ: [string, string][] = [
  ['cost', 'дороже сначала'],
  ['idle', 'дольше лежит'],
  ['qty', 'больше штук'],
  ['name', 'по наименованию'],
  ['wh', 'по складу'],
]

function Позиции({ ф }: { ф: Record<string, unknown> }) {
  const [сорт, setСорт] = useState('cost')
  const [толькоМёртвые, setТолькоМёртвые] = useState(true)
  const [старт, setСтарт] = useState(0)
  const запрос = useNLПозиции({
    ...ф, sort: сорт, dead: толькоМёртвые, start: старт, limit: РАЗМЕР_СТРАНИЦЫ,
  })
  const д = запрос.data

  return (
    <>
      <FilterBar id="nl-items" style={{ marginBottom: 8 }}>
        <div className="f">
          <label htmlFor="nl-dead">Показывать</label>
          <select id="nl-dead" value={толькоМёртвые ? '1' : '0'}
                  onChange={(e) => { setТолькоМёртвые(e.target.value === '1'); setСтарт(0) }}>
            <option value="1">только то, что не двигалось</option>
            <option value="0">весь остаток</option>
          </select>
        </div>
        <div className="f">
          <label htmlFor="nl-sort">Сортировка</label>
          <select id="nl-sort" value={сорт}
                  onChange={(e) => { setСорт(e.target.value); setСтарт(0) }}>
            {СОРТИРОВКИ.map(([к, п]) => <option key={к} value={к}>{п}</option>)}
          </select>
        </div>
      </FilterBar>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {д && (
          <>
            {д.total > 0 && (
              <div className="hint" style={{ marginBottom: 6 }}>
                {`Найдено ${д.total}. Показаны ${д.start + 1}–${Math.min(д.start + д.limit, д.total)}.`}
              </div>
            )}
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Наименование</th>
                    <th>Склад</th>
                    <th className="num">Кол-во</th>
                    <th className="num">Себестоимость</th>
                    <th className="num">Цена за ед.</th>
                    <th className="num">Дней без движения</th>
                    <th>Последнее движение</th>
                  </tr>
                </thead>
                <tbody>
                  {д.total === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty">
                        <div className="empty-title">
                          Под эти условия не попала ни одна позиция.
                        </div>
                        <div className="empty-hint">
                          Это ответ про фильтр, а не про склад — снимите порог или
                          расширьте регион.
                        </div>
                      </td>
                    </tr>
                  ) : д.rows.map((п) => <Строка key={п.stock_key} п={п} />)}
                </tbody>
              </table>
            </div>
            {/* `.pager` (styles.css §9), а не свой flex: на телефоне у него кнопки
                растягиваются на всю ширину (§12), а свой flex оставлял их шириной
                в слово — попасть пальцем в «← назад» рядом с «вперёд →» не выходило. */}
            <div className="pager">
              <button disabled={старт === 0}
                      onClick={() => setСтарт(Math.max(0, старт - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <button disabled={д.next === null}
                      onClick={() => setСтарт(д.next ?? старт)}>
                вперёд →
              </button>
            </div>
          </>
        )}
      </QueryState>
    </>
  )
}

function Строка({ п }: { п: Позиция }) {
  return (
    <tr>
      <td data-label="Наименование">
        {п.nom_name || <span className="muted">без названия</span>}
        {п.art && <div className="hint">арт. {п.art}</div>}
      </td>
      <td data-label="Склад">
        {п.wh_name}
        <div className="hint">{п.region || '(без региона)'}</div>
      </td>
      <td className="num" data-label="Кол-во">{п.qty ?? '—'}</td>
      {/* Пустая себестоимость — это НЕ ноль: позицию завели в 1С без цены. Показать здесь
          «0 ₽» значило бы посчитать её бесплатной, а её просто не оценили. */}
      <td className="num" data-label="Себестоимость">
        {п.cost === null
          ? <span className="muted" title="позиция заведена в 1С без себестоимости">нет цены</span>
          : fmtMoney(п.cost)}
      </td>
      <td className="num muted" data-label="Цена за ед.">
        {п.unit_cost === null ? '—' : fmtMoney(п.unit_cost)}
      </td>
      <td className="num" data-label="Дней без движения"
          style={{ fontWeight: п.dead ? 600 : 400 }}
          title={п.approx
            ? 'расхода по этой позиции не видели ни разу — лежит не меньше указанного'
            : `последний расход ${п.last_expense ?? '—'}`}>
        {возрастом(п)}
      </td>
      <td data-label="Последнее движение" className="hint">
        {п.last_expense
          ? `расход ${fmtDate(п.last_expense)}`
          : п.first_receipt
            ? `приход ${fmtDate(п.first_receipt)}, расхода не было`
            : 'движений не видели вовсе'}
      </td>
    </tr>
  )
}

// ───────────────────────────── резерв под заказ ─────────────────────────────

/**
 * Что здесь показано и чего здесь НЕТ.
 *
 * Показано: заказы клиентов, под которые в 1С держат резерв, и сколько дней они висят.
 * Это ответ на вопрос «склад занят под сделку, которая не движется».
 *
 * Нет: разбивки по позициям. Регистр отдаёт ссылки на номенклатуру без названий, а тянуть
 * названия по одной ссылке — это десятки запросов в 1С ради подписи. Вопрос, ради которого
 * вкладка сделана, звучит про ЗАКАЗ, и на него хватает свёртки.
 */
function Резерв() {
  const [минДней, setМинДней] = useState(30)
  const резерв = useРезерв()
  const ссылки = useMemo(() => (резерв.data?.orders ?? []).map((o) => o.ref), [резерв.data])
  const заказы = useЗаказыПодРезерв(ссылки)

  const строки = useMemo(() => {
    const по = new Map((резерв.data?.orders ?? []).map((o) => [o.ref, o]))
    return заказы.rows
      .map((з) => ({ з, р: по.get(з.ref_key) }))
      .filter((x) => x.р && (x.з.age_days ?? 0) >= минДней)
      .sort((a, b) => (b.з.age_days ?? 0) - (a.з.age_days ?? 0))
  }, [заказы.rows, резерв.data, минДней])

  const сумма = строки.reduce((s, x) => s + (x.з.sum_doc ?? 0), 0)

  return (
    <OdataState isLoading={резерв.isLoading} error={резерв.error}>
      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Эта вкладка читает 1С напрямую.</b> Резервов в зеркале нет — кабинет
        руководителя их не выгружает, поэтому здесь другой источник и другая свежесть.
        Сам заказ (номер, дата, сумма) приезжает из зеркала документов 1С.
      </div>
      <OdataFreshness meta={резерв.data?.meta} />

      <FilterBar id="nl-reserve" style={{ marginBottom: 8 }}>
        <div className="f">
          <label htmlFor="nl-res-age">Висит дольше</label>
          <select id="nl-res-age" value={минДней}
                  onChange={(e) => setМинДней(Number(e.target.value))}>
            {[0, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>{d === 0 ? 'любой срок' : `${d}+ дней`}</option>
            ))}
          </select>
        </div>
      </FilterBar>

      <QueryState isLoading={заказы.isLoading} error={заказы.error}>
        {(строки.length > 0 || заказы.missing.length > 0) && (
          <div className="hint" style={{ marginBottom: 6 }}>
            {строки.length > 0
              && `${pluralN(строки.length, 'заказ', 'заказа', 'заказов')}`
                + ` ${plural(строки.length, 'держит', 'держат', 'держат')} резерв,`
                + ` сумма заказов ${fmtMoney(сумма)}.`}
            {заказы.missing.length > 0
              && ` Ещё ${pluralN(заказы.missing.length, 'документ', 'документа', 'документов')}`
              + ` ${plural(заказы.missing.length, 'держит', 'держат', 'держат')} резерв,`
              + ' но в зеркале документов 1С их нет — показать про них нечего.'}
          </div>
        )}
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Заказ</th>
                <th className="num">Дней висит</th>
                <th className="num">Позиций в резерве</th>
                <th className="num">Штук</th>
                <th className="num">Сумма заказа</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {строки.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty">
                    <div className="empty-title">
                      Заказов, которые держат резерв дольше выбранного срока, нет.
                    </div>
                  </td>
                </tr>
              )}
              {строки.map(({ з, р }) => (
                <tr key={з.ref_key}>
                  <td data-label="Заказ">
                    {з.number || з.ref_key.slice(0, 8)}
                    <div className="hint">от {fmtDate(з.doc_date)}</div>
                  </td>
                  <td className="num" data-label="Дней висит"
                      style={{ fontWeight: (з.age_days ?? 0) >= 60 ? 600 : 400 }}>
                    {з.age_days ?? '—'}
                  </td>
                  <td className="num" data-label="Позиций в резерве">{р?.positions ?? '—'}</td>
                  <td className="num muted" data-label="Штук">{р?.qty ?? '—'}</td>
                  <td className="num" data-label="Сумма заказа">{fmtMoney(з.sum_doc)}</td>
                  <td data-label="Статус" className="muted">{з.status || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          «Сумма заказа» — сумма всего документа, а не только зарезервированных строк:
          состав документов 1С в зеркало не переносился. Считать её стоимостью резерва
          нельзя.
        </div>
      </QueryState>
    </OdataState>
  )
}
